import { Goal, GoalTreeIndex, ID } from '@griply/types';

export type GetSubGoalsOptions = {
  goals: Goal[];
  recursive?: boolean;
  index?: GoalTreeIndex;
};

const buildParentToChildrenMap = (goals: Goal[]): Map<ID, Goal[]> => {
  const parentToChildrenMap = new Map<ID, Goal[]>();
  goals.forEach((goal) => {
    if (!goal.parentIds?.length) {
      return;
    }
    const parentId = goal.parentIds[goal.parentIds.length - 1];
    const existing = parentToChildrenMap.get(parentId) ?? [];
    existing.push(goal);
    parentToChildrenMap.set(parentId, existing);
  });
  return parentToChildrenMap;
};

export const getSubGoals = (
  id: ID | undefined,
  { goals, recursive = false, index }: GetSubGoalsOptions,
): Goal[] => {
  if (!id) {
    return [];
  }

  const parentToChildrenMap =
    index?.childrenByParentId ?? buildParentToChildrenMap(goals);

  // The index's arrays outlive this call, so hand back a copy the caller may own.
  const directSubGoals = [...(parentToChildrenMap.get(id) || [])];

  if (!recursive) {
    return directSubGoals;
  }

  const processedIds = new Set<ID>(directSubGoals.map((goal) => goal.id));

  const collectDescendants = (currentLevel: Goal[]): Goal[] => {
    if (!currentLevel.length) {
      return [];
    }

    const nextLevel = currentLevel.flatMap((goal) =>
      (parentToChildrenMap.get(goal.id) || []).filter((child) => {
        if (processedIds.has(child.id)) {
          return false;
        }
        processedIds.add(child.id);
        return true;
      }),
    );

    return [...nextLevel, ...collectDescendants(nextLevel)];
  };

  return [...directSubGoals, ...collectDescendants(directSubGoals)];
};
